import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { createLead, LEAD_TYPES } from '../../services/leadService';

const roofTypes = [
  { value: 'ibr', label: 'IBR / Corrugated' },
  { value: 'tile', label: 'Tile' },
  { value: 'concrete', label: 'Concrete' },
];
const complexities = [
  { value: 'simple', label: 'Simple (Single storey, easy access)' },
  { value: 'moderate', label: 'Moderate (Double storey, some obstacles)' },
  { value: 'complex', label: 'Complex (Steep, multi-face, difficult access)' },
];

function calculateEstimate(numPanels, roofType, complexity) {
  const n = parseInt(numPanels) || 0;
  if (n <= 0) return null;
  let baseLaborCost = 4000;
  let laborPerPanel = 650;
  let mountingCostPerPanel = roofType === 'tile' ? 280 : roofType === 'concrete' ? 350 : 220;
  let sundriesPerPanel = 120;
  let cocCost = 1200;
  let complexityMultiplier = complexity === 'moderate' ? 1.15 : complexity === 'complex' ? 1.35 : 1.0;
  let totalMountingCost = n * mountingCostPerPanel;
  let totalLaborCost = baseLaborCost + (n * laborPerPanel);
  let totalSundriesCost = n * sundriesPerPanel;
  let subTotal = totalLaborCost + totalMountingCost + totalSundriesCost + cocCost;
  let finalCost = subTotal * complexityMultiplier;
  let minEstimate = Math.round(finalCost * 0.9 / 50) * 50;
  let maxEstimate = Math.round(finalCost * 1.15 / 50) * 50;
  if (minEstimate < 6000) minEstimate = 6000;
  if (maxEstimate < minEstimate + 2000) maxEstimate = minEstimate + 2000;
  return { min: minEstimate, max: maxEstimate };
}

const SolarInstallationCalculator = () => {
  const { isDarkMode } = useTheme();
  const [numPanels, setNumPanels] = useState(8);
  const [roofType, setRoofType] = useState('ibr');
  const [complexity, setComplexity] = useState('simple');
  const estimate = calculateEstimate(numPanels, roofType, complexity);

  // Lead gen form state
  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    numPanels: numPanels,
    roofType: roofType,
    complexity: complexity,
    message: ''
  });

  // Sync calculator values to form when opening
  const openForm = () => {
    setFormData(f => ({
      ...f,
      numPanels,
      roofType,
      complexity
    }));
    setShowForm(true);
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    try {
      // Store lead in Firestore
      const leadData = {
        ...formData,
        type: LEAD_TYPES.CALCULATOR,
        source: 'solar-installation-calculator',
        page: '/calculator',
        estimateMin: estimate.min,
        estimateMax: estimate.max
      };
      
      await createLead(leadData);
      console.log('Lead stored successfully:', leadData);
      
      setFormSubmitted(true);
      setTimeout(() => {
        setShowForm(false);
        setFormSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          numPanels: numPanels,
          roofType: roofType,
          complexity: complexity,
          message: ''
        });
      }, 2000);
    } catch (error) {
      console.error('Error storing lead:', error);
      alert('There was an error saving your information. Please try again.');
    }
  };

  return (
    <div className={`rounded-lg shadow-xl p-2 sm:p-6 w-full max-w-full mx-auto mb-8 sm:mb-12 transition-colors duration-300 ${isDarkMode ? 'bg-blue-800' : 'bg-amber-800'}`}>
      <h1 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-yellow-400">Solar Panel Installation Calculator</h1>
      <p className="text-center mb-6 sm:mb-8 text-sm sm:text-base text-white">Use this simplified calculator to get a rough estimate for your standalone solar panel installation. Remember, this is indicative and actual quotes will vary based on specific site conditions and installer pricing.</p>
      <div className="flex flex-col md:flex-row gap-4 sm:gap-8 items-start">
        <form className="flex-1 space-y-4 w-full">
          <div>
            <label className="block font-semibold mb-1 text-yellow-400 text-sm sm:text-base">Number of Panels (e.g., 6-12):</label>
            <input
              type="number"
              min="1"
              value={numPanels}
              onChange={e => setNumPanels(e.target.value)}
              className={`w-full p-2 sm:p-3 rounded-lg border ${isDarkMode ? 'border-blue-700 bg-blue-900 text-yellow-300 placeholder-yellow-200' : 'border-amber-700 bg-amber-100 text-amber-900 placeholder-amber-700'}`}
            />
          </div>
          <div>
            <label className="block font-semibold mb-1 text-yellow-400 text-sm sm:text-base">Roof Type:</label>
            <select
              value={roofType}
              onChange={e => setRoofType(e.target.value)}
              className={`w-full p-2 sm:p-3 rounded-lg border ${isDarkMode ? 'border-blue-700 bg-blue-900 text-yellow-300' : 'border-amber-700 bg-amber-100 text-amber-900'}`}
            >
              {roofTypes.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-semibold mb-1 text-yellow-400 text-sm sm:text-base">Installation Complexity:</label>
            <select
              value={complexity}
              onChange={e => setComplexity(e.target.value)}
              className={`w-full p-2 sm:p-3 rounded-lg border ${isDarkMode ? 'border-blue-700 bg-blue-900 text-yellow-300' : 'border-amber-700 bg-amber-100 text-amber-900'}`}
            >
              {complexities.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </form>
        <div className={`flex-1 rounded-lg p-4 sm:p-6 shadow text-center mt-4 md:mt-0 w-full ${isDarkMode ? 'bg-blue-900' : 'bg-amber-100'}`}>
          <h3 className="text-lg sm:text-xl font-bold mb-2 text-yellow-400">Estimated Installation Cost Range:</h3>
          <div className={`text-2xl sm:text-3xl font-extrabold mb-2 ${isDarkMode ? 'text-yellow-300' : 'text-amber-700'}`}>{estimate ? `R${estimate.min.toLocaleString()} – R${estimate.max.toLocaleString()}` : '--'}</div>
          <p className={`text-xs sm:text-sm mt-2 ${isDarkMode ? 'text-blue-100' : 'text-amber-900'}`}>This estimate includes labour, basic mounting for your chosen roof type, essential electrical sundries for the panel array, and a Certificate of Compliance. It excludes the cost of solar panels, inverter, batteries, major AC work, and SSEG application fees.</p>
          <button
            className={`mt-4 sm:mt-6 w-full py-2 sm:py-3 rounded-lg font-semibold text-base sm:text-lg transition-colors ${isDarkMode ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
            onClick={openForm}
            type="button"
          >
            Get a Free Quote
          </button>
        </div>
      </div>
      {/* Lead Gen Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50">
          <div className={`bg-white dark:bg-blue-800 rounded-lg shadow-xl px-6 py-8 sm:px-10 sm:py-12 max-w-sm sm:max-w-md w-full relative mt-10 sm:mt-16 overflow-y-auto max-h-[90vh]`}>
            <button
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-4xl font-extrabold text-yellow-400 bg-blue-900 dark:bg-yellow-400 dark:text-blue-900 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border-4 border-yellow-400 hover:bg-yellow-400 hover:text-blue-900 dark:hover:bg-blue-900 dark:hover:text-yellow-400 transition-colors duration-200 z-20 focus:outline-none focus:ring-4 focus:ring-yellow-300"
              style={{lineHeight: '1', padding: '0.25rem'}}
              onClick={() => setShowForm(false)}
              aria-label="Close"
              tabIndex={0}
            >
              ×
            </button>
            {formSubmitted ? (
              <div className="text-center py-8">
                <h3 className="text-2xl font-bold mb-2 text-blue-900 dark:text-yellow-400">Thank You!</h3>
                <p className="text-lg">We've received your request and will contact you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="text-2xl font-bold mb-4 text-center text-blue-900 dark:text-yellow-400">Get Your Free Installation Quote</h2>
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    required
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white placeholder-blue-300' : 'bg-gray-100 text-gray-900 placeholder-gray-500'}`}
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    required
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white placeholder-blue-300' : 'bg-gray-100 text-gray-900 placeholder-gray-500'}`}
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number"
                    required
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white placeholder-blue-300' : 'bg-gray-100 text-gray-900 placeholder-gray-500'}`}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Location (City/Suburb)"
                    required
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white placeholder-blue-300' : 'bg-gray-100 text-gray-900 placeholder-gray-500'}`}
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    name="numPanels"
                    value={formData.numPanels}
                    onChange={handleInputChange}
                    placeholder="Number of Panels"
                    min="1"
                    required
                    className={`w-1/2 p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white placeholder-blue-300' : 'bg-gray-100 text-gray-900 placeholder-gray-500'}`}
                  />
                  <select
                    name="roofType"
                    value={formData.roofType}
                    onChange={handleInputChange}
                    required
                    className={`w-1/2 p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white' : 'bg-gray-100 text-gray-900'}`}
                  >
                    {roofTypes.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                  </select>
                </div>
                <div>
                  <select
                    name="complexity"
                    value={formData.complexity}
                    onChange={handleInputChange}
                    required
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white' : 'bg-gray-100 text-gray-900'}`}
                  >
                    {complexities.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project (optional)"
                    rows="3"
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-blue-800 text-white placeholder-blue-300' : 'bg-gray-100 text-gray-900 placeholder-gray-500'}`}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className={`w-full py-3 rounded-lg font-semibold text-lg transition-colors ${isDarkMode ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
                >
                  Get Your Free Quote
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarInstallationCalculator; 